import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Camera } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { useImageCrop } from '@/hooks/useImageCrop';
import { useUploadFile } from '@/hooks/useUploadFile';
import type { UploadKind, UploadResult } from '@/api/uploads.api';
import { getUploadErrorMessage } from '@/lib/utils/upload-error.utils';

interface AvatarUploadFieldProps {
  photoUrl?: string;
  initials: string;
  uploadKind: UploadKind;
  accept: string;
  uploadCaption: string;
  /** Small guidance under the control, e.g. the recommended size (FR-116). */
  hint?: string;
  tooLargeMessage: string;
  errorMessage: string;
  /** Caller-supplied validation error (e.g. a required-field message from the surrounding form). Shown only while there's no upload-specific error to report. */
  error?: string;
  onUploaded: (result: UploadResult) => void;
  /**
   * Fired whenever an upload starts or finishes, so the SURROUNDING FORM can
   * disable its Save button while one is in flight.
   *
   * The upload runs inside this component, so without this the form has no way
   * to know one is happening - and saving mid-upload silently drops the image:
   * the key has not been set yet, so the payload omits it and the save
   * succeeds against the OLD file with no error anywhere. Reported in manual
   * testing as "it saved but the new logo never appeared".
   */
  onUploadingChange?: (isUploading: boolean) => void;
  /** Puts the avatar and its caption/hint side by side, left-aligned, for a form panel. Default is centered and stacked. */
  inline?: boolean;
}

/**
 * Editable circular avatar: click-to-upload (camera badge overlay + caption),
 * running the shared presign->PUT S3 flow (NFR-10). Domain-agnostic composite
 * (mnr-standards `common/`) so a second feature beyond Investor Profile can
 * reuse it without duplicating the upload wiring.
 */
export function AvatarUploadField({
  photoUrl,
  initials,
  uploadKind,
  accept,
  uploadCaption,
  hint,
  tooLargeMessage,
  errorMessage,
  error,
  onUploaded,
  onUploadingChange,
  inline = false,
}: AvatarUploadFieldProps) {
  const upload = useUploadFile();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onUploadingChange?.(upload.isPending);
  }, [upload.isPending, onUploadingChange]);

  // A PNG/JPEG is cropped to the kind's locked frame first (FR-121); cancelling the
  // dialog uploads nothing and keeps the current image.
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const crop = useImageCrop(uploadKind, (file) =>
    upload.mutate(
      { kind: uploadKind, file },
      {
        onSuccess: (result) => {
          // Show the file that was just uploaded (the cropped one), so a member who
          // crops and confirms sees the change at once rather than only after the save.
          setLocalPreviewUrl(URL.createObjectURL(file));
          onUploaded(result);
        },
      },
    ),
  );

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) crop.requestFile(file);
    event.target.value = ''; // allow re-selecting the same file
  };

  const message = upload.isError
    ? getUploadErrorMessage(upload.error, { tooLarge: tooLargeMessage, fallback: errorMessage })
    : error;

  return (
    <div className={inline ? 'flex items-center gap-4' : 'flex flex-col items-center gap-2'}>
      <div className="relative shrink-0">
        {/* Bespoke circular avatar-upload trigger - no Button variant covers a
            button-shaped-like-its-own-content control wrapping the Avatar
            primitive (mnr-standards UI Component Architecture, narrow
            exception). */}
        <button
          type="button"
          aria-label={uploadCaption}
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
          className="rounded-full border-2 border-dashed border-green disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Avatar photoUrl={localPreviewUrl ?? photoUrl} initials={initials} size={80} className="text-[22px]" />
        </button>
        <div className="pointer-events-none absolute -bottom-0.5 -right-0.5 flex size-7 items-center justify-center rounded-full border border-white/20 bg-navy/60">
          <Camera aria-hidden="true" size={16} className="text-white" />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
      {/* A percentage, not an ellipsis. The old caption changed to "…" while
          uploading, which on a large file over real bandwidth is far too quiet
          to stop someone hitting Save mid-transfer. Falls back to an
          indeterminate label when the transfer reports no total. */}
      <div className={inline ? 'flex min-w-0 flex-col gap-1' : 'contents'}>
        <p className={inline ? 'text-xs font-semibold text-green' : 'text-center text-[10px] text-green'}>
          {upload.isPending
            ? typeof upload.progress === 'number'
              ? `Uploading ${upload.progress}%`
              : 'Uploading…'
            : uploadCaption}
        </p>
        {hint && (
          <p className={inline ? 'text-xs text-slate' : 'max-w-[160px] text-center text-[10px] text-slate'}>
            {hint}
          </p>
        )}
        {message && (
          <p
            role="alert"
            className={inline ? 'text-xs text-danger' : 'max-w-[160px] text-center text-xs text-danger'}
          >
            {message}
          </p>
        )}
      </div>
      {crop.cropDialog}
    </div>
  );
}
